import type { IToolConfig, TApplyBackgroundProp } from 'pages/Batch/types';
import backgroundProcess from 'pages/Batch/utils/process/backgroundProcess';
import removeBgProcess from 'pages/Batch/utils/process/removeBgProcess';
import aiBackgroundProcess from 'pages/Batch/utils/process/aiBackgroundProcess';
import { resizeImage } from 'pages/Batch/utils/process/resizeProcess';
import cropImage from 'pages/Batch/utils/process/cropImage';
import { BATCH_ACTIONS } from 'pages/Batch/configs/index';
import enhanceByIntensity from 'pages/Batch/utils/process/enhanceByIntensity';
import { getWfxProcess } from 'pages/Batch/utils/process/wfxProcess';
import type { WfxProcessAttrs } from 'pages/Batch/utils/process/wfxProcess';
import { getEditorLibProcess } from 'pages/Batch/utils/process/editorLibProcess';
import type { EditorLibProcessAttrs } from 'pages/Batch/utils/process/editorLibProcess';
import { getAdjustProcess } from 'pages/Batch/utils/process/adjustProcess';
import type { AdjustProcessAttrs } from 'pages/Batch/utils/process/adjustProcess';
import { getComposedEnhanceProcess } from 'pages/Batch/utils/process/composedEnhanceProcess';
import type { ComposedEnhanceAttrs } from 'pages/Batch/utils/process/composedEnhanceProcess';

import pluggableApisService from 'services/pluggableApis';

import type { ICropFlip, ICropAttrsBatch } from 'types/store';

import partial from 'lodash/partial';
import watermarkGenerator from '../utils/watermark/watermarkGenerator';

type TProp = {
  width: number;
  height: number;
  usePadding?: boolean;
  bgColor: string | null;
};
type ApplyEnhanceProp = {
  enhanceMapping: Record<string, string>;
  intensity: number;
  itemIntensities?: Record<string, number>;
};
type CropProp = { attrs: ICropAttrsBatch[]; rotate: number; flip: ICropFlip };

const toolsConfig: { [key in BATCH_ACTIONS]: IToolConfig } = {
  [BATCH_ACTIONS.removeBackground]: {
    getProcess: () => (_: HTMLImageElement, isCancel: () => boolean, itemKey?: string) => removeBgProcess(itemKey as string, isCancel),
  },
  [BATCH_ACTIONS.applyBackground]: {
    getProcess:
      ({ texture, color, objectPosition, ratio, foregroundTransforms, foregroundContentBboxes }: TApplyBackgroundProp) =>
      (image: HTMLImageElement, _isCancel: () => boolean, itemKey?: string) =>
        backgroundProcess(image, {
          texture,
          color,
          objectPosition,
          ratio,
          foregroundTransform: itemKey ? foregroundTransforms?.[itemKey] : undefined,
          foregroundContentBbox: itemKey ? foregroundContentBboxes?.[itemKey] : undefined,
        }),
    usePending: true,
  },
  [BATCH_ACTIONS.generateAIBackground]: {
    getProcess:
      ({ prompt }: { prompt: string }) =>
      (_: HTMLImageElement, isCancel: () => boolean, itemKey?: string) =>
        aiBackgroundProcess(prompt, itemKey as string, isCancel),
  },
  [BATCH_ACTIONS.resize]: {
    getProcess:
      ({ width, height, usePadding, bgColor }: TProp) =>
      (image: HTMLImageElement) =>
        resizeImage(image, width, height, usePadding, bgColor),
  },
  [BATCH_ACTIONS.applyCrop]: {
    getProcess:
      ({ attrs, rotate, flip }: CropProp) =>
      // @ts-ignore
      (image: HTMLImageElement, isCancel: () => boolean, key?: string) =>
        cropImage(image, attrs, rotate, flip, key as string),
  },
  [BATCH_ACTIONS.aiEnhance]: {
    getProcess: () => (image: HTMLImageElement, isCancel: () => boolean, itemKey?: string) => partial(pluggableApisService.enhanceImage.bind(pluggableApisService))(image, itemKey as string, isCancel),
    saveAsEnhanced: true,
  },
  [BATCH_ACTIONS.applyEnhance]: {
    getProcess:
      ({ intensity, enhanceMapping, itemIntensities }: ApplyEnhanceProp) =>
      (image: HTMLImageElement, _isCancel: () => boolean, itemKey?: string) => {
        const itemIntensity = itemKey && itemIntensities?.[itemKey] !== undefined ? itemIntensities[itemKey] : intensity;
        return enhanceByIntensity(image, enhanceMapping, itemIntensity);
      },
  },
  [BATCH_ACTIONS.watermark]: {
    getProcess: watermarkGenerator.generate.bind(watermarkGenerator),
  },
  [BATCH_ACTIONS.wfx]: {
    getProcess: (attrs: WfxProcessAttrs & Partial<EditorLibProcessAttrs>) => {
      if (attrs.editorLibCategoryId) {
        return (image: HTMLImageElement, isCancel: () => boolean) => getEditorLibProcess(attrs as EditorLibProcessAttrs)(image, isCancel);
      }
      return (image: HTMLImageElement, isCancel: () => boolean) => getWfxProcess(attrs)(image, isCancel);
    },
  },
  [BATCH_ACTIONS.applyWfx]: {
    getProcess: () => async (image: HTMLImageElement) => ({ result: image.src }),
    usePending: true,
  },
  [BATCH_ACTIONS.adjust]: {
    getProcess: (attrs: AdjustProcessAttrs) => {
      return (image: HTMLImageElement, isCancel: () => boolean) => getAdjustProcess(attrs)(image, isCancel);
    },
  },
  [BATCH_ACTIONS.applyAdjust]: {
    getProcess: () => async (image: HTMLImageElement) => ({ result: image.src }),
    usePending: true,
  },
  [BATCH_ACTIONS.composedEnhance]: {
    getProcess: (attrs: ComposedEnhanceAttrs) => {
      return (image: HTMLImageElement, isCancel: () => boolean, itemKey?: string) => getComposedEnhanceProcess(attrs)(image, isCancel, itemKey);
    },
    useEnhanced: true,
  },
} as const;

export default toolsConfig;
